package ui.device.detail.preview.screen

import androidx.compose.animation.*
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.Content_save
import compose.assets.svg.Download
import device.DevicePlaylist
import io.Repository
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.burnoutcrew.reorderable.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.navTo_CampaignPreviewUI
import tools.FilePicker
import tools.await
import tools.rememberString
import tools.uploadZip
import ui.campaign.detailv2.preview.CampaignPreviewArgs
import ui.device.detail.preview.DevicePreviewScope
import ui.device.detail.preview.DevicePreviewVM
import ui.snackBarStateGlobal
import ui.widget.*

@Composable
fun DevicePreviewScope.DeviceScreen02_Playlist(nnvl: DevicePreviewScope.Wrapper) {

    val excelDownloaderState = DialogState.remember()

    val stateHorizontal = rememberScrollState(0)
    val reorderState = rememberReorderableLazyListState(
        onMove = { from, to -> nnvl.adapterPlaylist.swapOrder(from.index, to.index) },
        canDragOver = { current, actual -> nnvl.adapterPlaylist.state[current.index].data.isCommercial == nnvl.adapterPlaylist.state[actual.index].data.isCommercial })

    DialogExcelDownloader(excelDownloaderState, args.deviceId)

    val maxBoxSize = remember {
        mutableStateOf<Dp?>(null)
    }

    val density = LocalDensity.current
    Box(
        Modifier.fillMaxSize()
            .onGloballyPositioned {
                maxBoxSize.value = it.parentCoordinates?.boundsInWindow()?.let {
                    ((it / density.density).width - (16 * 2)).dp
                }
            }) {
        LazyColumn(
            state = reorderState.listState,
            verticalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(horizontal = 16.dp),
            modifier = Modifier
                .reorderable(reorderState)
                .fillMaxHeight()
                .horizontalScroll(stateHorizontal)
        ) {
            itemsIndexed(
                items = nnvl.adapterPlaylist.state,
                key = { _, it -> it.key },
            ) { index, item ->
                ReorderableItem(
                    reorderableState = reorderState,
                    key = item.key,
                    index = index
                ) { isDragging ->
                    val elevation = animateDpAsState(if (isDragging) 8.dp else 0.dp)
                    val color = animateColorAsState(
                        when {
                            isDragging -> MaterialTheme.colorScheme.surfaceContainer
                            item.data.isCommercial -> MaterialTheme.colorScheme.primaryContainer
                            else -> MaterialTheme.colorScheme.tertiaryContainer
                        }
                    )
                    val prefix = remember(item.data.isCommercial) {
                        if (item.data.isCommercial) "C"
                        else "N"
                    }
                    val suffix = remember(index) {
                        if (item.data.isCommercial) index
                        else (index - nnvl.adapterPlaylist.commercialCount)
                    }
                    Column {
                        AnimatedVisibility(
                            visible = !isDragging,
                            enter = expandVertically() + fadeIn(),
                            exit = shrinkVertically() + fadeOut(),
                        ) {
                            Text(
                                text = item.data.nameCampaign,
                                style = MaterialTheme.typography.labelSmall,
                                modifier = Modifier.padding(bottom = 2.dp, top = 4.dp)
                            )
                        }

                        ElevatedCard(
                            elevation = CardDefaults.cardElevation(
                                defaultElevation = elevation.value,
                                pressedElevation = elevation.value,
                                focusedElevation = elevation.value,
                                hoveredElevation = elevation.value,
                                draggedElevation = elevation.value,
                                disabledElevation = elevation.value
                            ),
                            colors = CardDefaults.cardColors(color.value)
                        ) {
                            PlayListEntry(
                                maxBoxSize = maxBoxSize,
                                label = "${prefix}${suffix + 1}",
                                item = item,
                                reorderState = reorderState
                            )
                        }
                    }
                }
            }
        }
        HorizontalScrollbar(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(bottom = 8.dp)
                .fillMaxWidth(),
            adapter = rememberScrollbarAdapter(stateHorizontal)
        )
        val downloadButtonPadding by animateDpAsState(
            if (nnvl.adapterPlaylist.change) 72.dp
            else 0.dp
        )
        if (nnvl.adapterPlaylist.state.isEmpty()) {
            Text(
                text = Res.string.device_detail__layer_top__no_campaign.rememberString(),
                style = MaterialTheme.typography.displaySmall,
                modifier = Modifier.align(Alignment.Center)
            )
        }
        if (nnvl.adapterPlaylist.state.isNotEmpty()) {
            FloatingActionButton(
                onClick = { excelDownloaderState.show() },
                modifier = Modifier.align(Alignment.BottomEnd)
                    .padding(bottom = downloadButtonPadding)
                    .padding(16.dp)
            ) { Icon(Svg.Download, null) }
        }

        AnimatedVisibility(
            visible = nnvl.adapterPlaylist.change,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier.align(Alignment.BottomEnd)
                .padding(16.dp)
        ) {
            FloatingActionButton(
                onClick = {
                    action(DevicePreviewVM.Action.ReorderPlaylist)
                },
                modifier = Modifier.align(Alignment.BottomEnd)
            ) { Icon(Svg.Content_save, null) }
        }

        AnimatedVisibility(
            visible = nnvl.adapterPlaylist.uploadingChanges,
            modifier = Modifier.fillMaxSize()
        ) {
            LoadingOverlayUI()
        }
    }
}

@Composable
private fun DialogExcelDownloader(state: DialogState, deviceId: Long) {
    Dialog(state) {
        val scope = rememberCoroutineScope()

        var isGenerating by remember {
            mutableStateOf(false)
        }

        fun generate() {
            scope.launch {
                runCatching {
                    val data = Repository.api.playlist.downloadExcel(deviceId)
                    if (isActive && state.visible) {
                        FilePicker.uploadZip(data, Res.string.device_detail__layer_top__excel_file_name.await())
                        state.hide()
                    }
                }.onFailure {
                    isGenerating = false
                    snackBarStateGlobal.showSnackbar(
                        it.message ?: Res.string.err_fail_unexpected.await(),
                        withDismissAction = true
                    )
                }
            }
        }
        DialogUI(
            Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = { Text(Res.string.device_detail__layer_top__excel_preview_title.rememberString()) },
            dismissButton = {
                AnimatedVisibility(
                    visible = !isGenerating,
                    enter = fadeIn() + expandVertically(),
                    exit = fadeOut() + shrinkVertically()
                ) {
                    TextButton({ state.hide() }) {
                        Text(Res.string.dialog__action_cancel.rememberString())
                    }
                }
            },
            confirmButton = {
                AnimatedVisibility(
                    visible = !isGenerating,
                    enter = fadeIn() + expandVertically(),
                    exit = fadeOut() + shrinkVertically()
                ) {
                    TextButton({
                        isGenerating = true
                        generate()
                    }) {
                        Text(Res.string.device_detail__layer_top__generate.rememberString())
                    }
                }
            }
        ) {
            Crossfade(isGenerating) {
                Box(Modifier.fillMaxWidth().defaultMinSize(200.dp, 64.dp).align(Alignment.Center)) {
                    if (it) CircularProgressIndicator(Modifier.align(Alignment.Center))
                    else Text(Res.string.device_detail__layer_top__excel_preview_message.rememberString())
                }
            }
        }
    }
}

private fun DevicePlaylist.Visual.calculateSize(heightExpect: Int): DpSize {
    val ratio = if (isLand) width.toFloat() / height.toFloat()
    else height.toFloat() / width.toFloat()
    return DpSize((ratio * heightExpect.toFloat()).dp, heightExpect.dp)
}

@Composable
fun DevicePreviewScope.PlayListEntry(
    label: String,
    item: DevicePreviewScope.WrapperPlaylist.Item,
    reorderState: ReorderableLazyListState,
    maxBoxSize: State<Dp?>
) {

    val size = remember(item.data.primary.resourceId) {
        item.data.primary.calculateSize(heightExpect = 80)
    }

    val contentWidth = remember(maxBoxSize.value, item.key) {
        val realWidth = 80F +
                size.width.value +
                (if (item.data.alternative.isEmpty()) 0F else 12F) +
                item.data.alternative.sumOf { 4.0 } +
                (item.data.alternative.sumOf { it.calculateSize(80).width.value.toDouble() })

        if (realWidth > (maxBoxSize.value?.value ?: 0F)) {
            realWidth.dp
        } else (maxBoxSize.value ?: 258.dp)
    }

    Row(Modifier.width(contentWidth)) {
        Box(
            Modifier.size(80.dp)
                .detectReorder(reorderState)
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.displaySmall,
                textAlign = TextAlign.Center,
                modifier = Modifier.align(Alignment.Center)
            )
        }
        Row(Modifier.clickable {
            nav.navTo_CampaignPreviewUI(
                args = CampaignPreviewArgs(
                    companyId = item.data.companyId,
                    campaignGroupId = item.data.campaignGroupId
                )
            )
        }) {
            LazyImage(
                id = item.data.primary.resourceId,
                modifier = Modifier.size(size),
                alphaBackground = false
            )
            item.data.alternative.forEachIndexed { index, visual ->
                if (index == 0) Space(16.dp)
                else Space(4.dp)
                val sizeAlt = remember(visual.resourceId) {
                    visual.calculateSize(heightExpect = 80)
                }
                LazyImage(
                    id = visual.resourceId,
                    modifier = Modifier.size(sizeAlt),
                    alphaBackground = false
                )
            }
        }
    }
}